/**
 * Client-side conversion helpers mirroring ProductPackagingConversionService.
 * Inventory truth remains kg; secondary/packaging qtys are derived.
 */

import { formatStockQuantity } from "./stockNumberFormat";

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function hasSecondaryUnit(product) {
  if (!product) {
    return false;
  }

  if (product.has_secondary_unit === true) {
    return true;
  }

  return !!(product.secondary_unit_id && toNumber(product.secondary_unit_kg) > 0);
}

export function packagingLevels(product) {
  const levels = Array.isArray(product?.packaging_levels) ? product.packaging_levels : [];
  return [...levels].sort((a, b) => toNumber(a.level) - toNumber(b.level));
}

export function breakdownFromKg(product, kg) {
  const netKg = toNumber(kg);
  const secondaryEnabled = hasSecondaryUnit(product);
  const secondaryUnitKg = secondaryEnabled ? toNumber(product.secondary_unit_kg) : null;

  return {
    kg: netKg,
    secondary_unit_id: secondaryEnabled ? product.secondary_unit_id : null,
    secondary_unit_code: secondaryEnabled
      ? product.secondary_unit?.code || product.secondary_unit_code || null
      : null,
    secondary_unit_kg: secondaryUnitKg,
    secondary_quantity: secondaryEnabled && secondaryUnitKg > 0 ? netKg / secondaryUnitKg : null,
    packaging_levels: packagingLevels(product).map((level) => {
      const weightKg = toNumber(level.weight_kg);
      return {
        level: toNumber(level.level),
        packaging_id: level.packaging_id || level.stock_packaging_id || null,
        packaging_name: level.packaging?.label || level.packaging?.name || level.packaging_name || null,
        weight_kg: weightKg,
        quantity: weightKg > 0 ? netKg / weightKg : null,
      };
    }),
  };
}

export function kgFromSecondary(product, secondaryQty) {
  if (!hasSecondaryUnit(product)) {
    return 0;
  }

  return toNumber(secondaryQty) * toNumber(product.secondary_unit_kg);
}

export function secondaryUnitCode(product) {
  if (!hasSecondaryUnit(product)) {
    return null;
  }

  return product.secondary_unit?.code || product.secondary_unit_code || null;
}

/**
 * Inventory price is stored per kg. One secondary unit (for example 1 pcs)
 * costs that kg price times the kg contained in the unit.
 */
export function secondaryPriceFromKgPrice(product, kgPrice) {
  if (!hasSecondaryUnit(product)) {
    return null;
  }

  const unitKg = toNumber(product.secondary_unit_kg);
  if (unitKg <= 0) {
    return null;
  }

  return toNumber(kgPrice) * unitKg;
}

export function kgPriceFromSecondaryPrice(product, secondaryPrice) {
  if (!hasSecondaryUnit(product)) {
    return 0;
  }

  const unitKg = toNumber(product.secondary_unit_kg);
  if (unitKg <= 0) {
    return 0;
  }

  return toNumber(secondaryPrice) / unitKg;
}

export function kgFromPackagingLevel(product, levelNumber, qty) {
  const level = packagingLevels(product).find((item) => toNumber(item.level) === toNumber(levelNumber));
  if (!level || toNumber(level.weight_kg) <= 0) {
    return 0;
  }

  return toNumber(qty) * toNumber(level.weight_kg);
}

/**
 * @param {'kg'|'secondary'|number|string} changedField
 */
export function syncFromChangedField(product, changedField, value) {
  let kg = 0;

  if (changedField === "kg") {
    kg = toNumber(value);
  } else if (changedField === "secondary") {
    kg = kgFromSecondary(product, value);
  } else {
    kg = kgFromPackagingLevel(product, changedField, value);
  }

  return breakdownFromKg(product, kg);
}

export function formatQty(qty, decimals = 4) {
  return formatStockQuantity(qty, decimals);
}

export function breakdownDisplayRows(breakdown) {
  if (!breakdown) {
    return [];
  }

  const rows = [];

  if (breakdown.secondary_quantity != null && breakdown.secondary_unit_code) {
    rows.push({
      key: "secondary",
      name: breakdown.secondary_unit_code,
      quantity: breakdown.secondary_quantity,
      weightKg: breakdown.secondary_unit_kg,
    });
  }

  (breakdown.packaging_levels || []).forEach((level) => {
    if (level.quantity == null || !level.packaging_name) {
      return;
    }

    rows.push({
      key: `pkg-${level.level}-${level.packaging_id || level.packaging_name}`,
      name: level.packaging_name,
      quantity: level.quantity,
      weightKg: level.weight_kg,
    });
  });

  return rows;
}

export function formatBreakdownLabel(breakdown, decimals = 4) {
  const rows = breakdownDisplayRows(breakdown);
  if (!rows.length) {
    return breakdown?.label || null;
  }

  return rows.map((row) => `${formatQty(row.quantity, decimals)} ${row.name}`).join(" · ");
}

export function applyBreakdownToLine(line, breakdown) {
  line.quantity = breakdown.kg;
  line.secondary_quantity = breakdown.secondary_quantity;
  line.packaging_quantities = {};
  (breakdown.packaging_levels || []).forEach((level) => {
    line.packaging_quantities[level.level] = level.quantity;
  });
  return line;
}

export default {
  breakdownFromKg,
  kgFromSecondary,
  kgFromPackagingLevel,
  secondaryUnitCode,
  secondaryPriceFromKgPrice,
  kgPriceFromSecondaryPrice,
  syncFromChangedField,
  formatBreakdownLabel,
  breakdownDisplayRows,
  applyBreakdownToLine,
  hasSecondaryUnit,
  packagingLevels,
  formatQty,
};
